'use client';

import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ensureSessionReady, SupabaseOperationError } from '@/lib/supabase';

export function QueryProvider({ children }: { children: React.ReactNode }) {
  const recoveryRunningRef = useRef(false);
  const lastRecoveryAtRef = useRef(0);

  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            // Dados válidos por 30 segundos
            staleTime: 30 * 1000,
            // Manter dados em cache por 10 minutos
            gcTime: 10 * 60 * 1000,
            // Retry inteligente: evita repetir erro estrutural de auth/RLS.
            retry: (failureCount, error) => {
              if (error instanceof SupabaseOperationError) {
                if (error.kind === 'auth' || error.kind === 'rls') return false;
              }

              return failureCount < 2;
            },
            retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
            // Refetch quando a janela ganha foco (resolve skeleton infinito após inatividade)
            refetchOnWindowFocus: true,
            // Refetch quando reconecta após queda de rede
            refetchOnReconnect: true,
            // Refetch ao montar componente se dados estão stale
            refetchOnMount: true,
            // CRÍTICO: Sempre tentar fetch, mesmo que o browser ache que está offline.
            // Em redes locais (192.168.x.x) o navigator.onLine pode dar falso-negativo.
            networkMode: 'always',
          },
          mutations: {
            networkMode: 'always',
          },
        },
      })
  );

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const recoverStaleSessionAndQueries = async () => {
      if (document.visibilityState === 'hidden') return;

      const now = Date.now();
      // Debounce simples para evitar tempestade de refetch ao alternar abas.
      if (recoveryRunningRef.current || now - lastRecoveryAtRef.current < 1500) return;

      recoveryRunningRef.current = true;
      lastRecoveryAtRef.current = now;

      try {
        await ensureSessionReady();

        await queryClient.cancelQueries({
          predicate: (query) => query.state.fetchStatus === 'fetching',
        });

        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['contracts'] }),
          queryClient.invalidateQueries({ queryKey: ['commissions'] }),
          queryClient.invalidateQueries({ queryKey: ['commissions-kpis'] }),
        ]);
      } catch {
        // A camada de UI/lógica de auth decide como reagir ao erro final.
      } finally {
        recoveryRunningRef.current = false;
      }
    };

    const handleFocus = () => {
      void recoverStaleSessionAndQueries();
    };

    const handleVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      void recoverStaleSessionAndQueries();
    };

    const handleOnline = () => {
      void recoverStaleSessionAndQueries();
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
    </QueryClientProvider>
  );
}
